import { useRef } from "react";
import { ShipmentModel } from "features/shared/shipmentCard.types";
import { usePanelViews } from "features/shared/usePanelViews.hook";
import { appStore } from "entities/App";
import { authStore } from "entities/Auth";
import { GetSeaCardsFunc, seaSavedCardStore } from "entities/SeaSavedCard";
import {
  createSeaSearchQueryParams,
  SeaQueryParams,
  seaSearchCardStore,
  SeaSearchRequestFunc,
} from "entities/SeaSearchCard";
import { useAppDispatch, useAppSelector } from "shared/lib/redux";
import { RequiredBy } from "shared/lib/typeCheck.lib";

import { getInitialSearchQPM } from "../ParamsManager";
import { getSeaCarrierCodeFromUrl, getSeaReferenceFromUrl, getSeaReferenceTypeFromUrl } from "../urlQueryParam.utils";

type UseUrlQueryBasedInitialSearchProps = {
  searchController: {
    searchRequest: SeaSearchRequestFunc;
    getCards: GetSeaCardsFunc;
    isSearchLoading: boolean;
  };
  openedCardAction: (card: ShipmentModel | null) => { type: string; payload: ShipmentModel | null };
};

type UseSeaUrlQueryBasedInitialSearchReturnType = {
  seaInitialSearch: ({ requestedFromUrl }: { requestedFromUrl: boolean }) => void;
};

export const useSeaUrlQueryBasedInitialSearch = ({
  searchController,
  openedCardAction,
}: UseUrlQueryBasedInitialSearchProps): UseSeaUrlQueryBasedInitialSearchReturnType => {
  const dispatch = useAppDispatch();
  const isAuthenticated = useAppSelector(authStore.selectors.isAuthenticated);
  const searchCards = useAppSelector(seaSearchCardStore.selectors.searchCards);
  const { showOpenedCardView, showMainView, showIntroView } = usePanelViews();
  const { searchRequest, getCards, isSearchLoading } = searchController;
  const initialSearchQPM = useRef(getInitialSearchQPM()).current;

  const createRequestFuncByStrategy = (
    strategy: "authenticated" | "not-authenticated",
    requestParams: RequiredBy<SeaQueryParams, "reference_number">
  ): (() => void) | null => {
    const reqParams = createSeaSearchQueryParams({
      model: {
        reference: requestParams.reference_number,
        carrierScac: requestParams.carrier_code,
        referenceType: requestParams.reference_type,
      },
      requestType: "search",
    });
    if (!reqParams) return null;
    switch (strategy) {
      case "authenticated":
        return () => {
          getCards(
            { where: { reference: requestParams.reference_number } },
            {
              onSettled: (userSearchResponse) => {
                searchRequest(reqParams, {
                  onSuccess: (searchResponse) => {
                    if (searchResponse.requestLimitReached) return;
                    dispatch(openedCardAction(searchResponse));
                  },
                  onSettled: (searchResponse) => {
                    if (userSearchResponse?.length) {
                      dispatch(seaSavedCardStore.actions.prependSavedCardAction({ data: userSearchResponse[0] }));
                      return;
                    }

                    if (searchResponse && !searchResponse?.requestLimitReached) {
                      dispatch(seaSearchCardStore.actions.prependSearchCardAction({ data: searchResponse }));
                    }
                  },
                });
              },
            }
          );
        };
      case "not-authenticated":
      default:
        return () => {
          searchRequest(reqParams, {
            onWebAppRequestLimitReached: () => {
              if (searchCards.length > 0) {
                showMainView();
              } else {
                showIntroView();
              }
            },
            onSuccess: (searchResponse) => {
              dispatch(seaSearchCardStore.actions.prependSearchCardAction({ data: searchResponse }));
              dispatch(openedCardAction(searchResponse));
            },
          });
        };
    }
  };

  const seaInitialSearch = ({ requestedFromUrl }: { requestedFromUrl: boolean }) => {
    if (!requestedFromUrl) return;

    const seaReferenceQueryValue = getSeaReferenceFromUrl(initialSearchQPM);

    if (!seaReferenceQueryValue) return;

    dispatch(appStore.actions.setRequestedFromUrl(true));

    showOpenedCardView();

    const requestFunc = createRequestFuncByStrategy(isAuthenticated ? "authenticated" : "not-authenticated", {
      reference_number: seaReferenceQueryValue,
      reference_type: getSeaReferenceTypeFromUrl(initialSearchQPM) ?? undefined,
      carrier_code: getSeaCarrierCodeFromUrl(initialSearchQPM) ?? undefined,
    });

    requestFunc?.();
  };

  return { seaInitialSearch };
};
